import * as __req_0 from "redux-form/lib/actions";
import React, { Component } from "react";
import { Field, FieldArray, reduxForm, touch, stopSubmit } from "redux-form";
import { connect } from "react-redux";
import { Form, Alert } from "react-bootstrap";
import ButtonWithLoad from "@common/components/ButtonWithLoad/ButtonWithLoad";
import { showLoading, hideLoading } from "react-redux-loading-bar";
import { debounce } from "lodash";

import Error from "./Error";
import Spinner from "@common/components/Spinner/Spinner";
import BrewWater from "./BrewWater/BrewWater";
import Mashing from "./Mashing/Mashing";
import Boiling from "./Boiling/Boiling";
import Fermentation from "./Fermentation/Fermentation";
import initialValues from "./initialValues";
import CustomField from "../CustomField/CustomField";
import Co2ConverterModal from "./Co2ConverterModal";
import { validateRecipe } from "@beers/services/recipeService";
import { get } from "@common/services/request";
import { apiEndpoints as api } from "@common/constants/urls";
import {
  MAX_FERMENTING_STEPS,
  MAX_MASH_STAGES,
  FERMENTABLE_TYPE,
  VALIDATION,
} from "@common/constants/recipeConstants";
import {
  validateIngredients,
  normalizeNumber,
  countFermentationSteps,
  countMashStages,
} from "../../../../util/recipeHelper";
import {
  calculateTotalMashInWater,
  calculateTotalWater,
  calculateKettleWater,
  calculateBeerProfile,
} from "../utils";
import { numberBetween } from "../../../../util/formValidationFunctions";

import "./Recipe.scss";

const normalizeServingTemp = normalizeNumber(2, 2);
const normalizeChillTemp = normalizeNumber(2, 1);
const chillTemp = numberBetween(
  VALIDATION.MIN_CHILL_TEMP,
  VALIDATION.MAX_CHILL_TEMP,
);
const { clearSubmitErrors } = __req_0.default;

class Recipe extends Component {
  state = {
    isLoading: true,
    netError: false,
    batchSize: 6,
    preMixWater: 1.0,
    totalWater: 0,
    kettleWater: 0,
    efficiency: 0,
    calcWater: true,
    showCo2ConverterModal: false,
  };

  componentDidMount() {
    this.props.dispatch(showLoading());
    if (this.props.recipeId === "new") {
      this.props.initialize({
        ...initialValues,
        carbonation: this.props.beerCarbonation || "",
      });
      this.props.dispatch(hideLoading());
      this.setState({ isLoading: false });
      return;
    }

    get(`${api.recipes}${this.props.recipeId}/`)
      .then((res) => {
        const recipe = validateIngredients(res);
        this.props.initialize({
          ...recipe,
          carbonation:
            (res.carbonation && parseFloat(res.carbonation) > 0
              ? res.carbonation
              : "") ||
            (this.props.beerCarbonation &&
            parseFloat(this.props.beerCarbonation) > 0
              ? this.props.beerCarbonation
              : "") ||
            "",
        });
        this.props.dispatch(hideLoading());
        this.setState({ isLoading: false });
        // Trigger first validation
        // this.props.shouldValidateInitial && this.props.handleSubmit();

        if (this.props.shouldValidateInitial) {
          validateRecipe(recipe)
            .then(() => {})
            .catch((e) => {
              e.then((err) => {
                // Manually trigger stopSubmit to add the validation errors
                // to the redux store
                this.props.dispatch(stopSubmit("recipe", err.errors));
              });
            });
        }

        // Touch all fields so validation get's triggered
        this.props.shouldValidateInitial &&
          Object.keys(this.props.form.recipe.registeredFields).forEach(
            (item) => {
              this.props.touch(item);
            },
          );
      })
      .catch(() => {
        this.setState({ isLoading: false, netError: true });
        this.props.dispatch(hideLoading());
      });
  }

  componentWillReceiveProps(nextProps) {
    if (
      nextProps.beerCarbonation &&
      parseFloat(nextProps.beerCarbonation) > 0 &&
      nextProps.form.recipe &&
      (!nextProps.form.recipe.values ||
        !nextProps.form.recipe.values.carbonation ||
        nextProps.form.recipe.values.carbonation === "" ||
        +nextProps.form.recipe.values.carbonation === 0)
    ) {
      this.props.change("carbonation", nextProps.beerCarbonation);
    }
    if (nextProps.form.recipe) {
      if (nextProps.form.recipe.values !== this.props.form.recipe.values) {
        this.updateStore(nextProps);
        if (this.props.shouldValidateInitial) {
          const recipe = JSON.parse(
            JSON.stringify(nextProps.form.recipe.values),
          );
          recipe.fg = recipe.fg ? parseFloat(recipe.fg).toFixed(3) : 0;
          recipe.og = recipe.og ? parseFloat(recipe.og).toFixed(3) : 0;
          recipe.ibu = recipe.ibu ? parseInt(recipe.ibu) : 0;
          recipe.beer_id = this.props.routing.location.pathname.split("/")[2];

          this.validate(recipe);
        }
        if (this.state.calcWater) {
          this.calculateWaterAmount(nextProps);
        }
      }
    }
  }

  componentWillUnmount() {
    this.setState({ calcWater: false });
  }

  handleApplyCo2Conversion = (convertedGPerL) => {
    this.props.change("carbonation", convertedGPerL);
    this.setState({ showCo2ConverterModal: false });
  };

  validate = debounce(
    (recipe) => {
      validateRecipe(recipe)
        .then(() => {
          this.props.dispatch(clearSubmitErrors("recipe"));
        })
        .catch((e) => {
          e.then((err) => {
            this.props.dispatch(stopSubmit("recipe", err.errors));
          });
        });
    },
    300,
    { trailing: true },
  );

  updateStore = debounce(
    (nextProps) => {
      const beerProfile = calculateBeerProfile(nextProps.form.recipe);
      if (beerProfile) {
        const efficiency = beerProfile[1] ? beerProfile[1] : 0.0;
        const { change } = this.props;

        if (this.state.efficiency !== efficiency) {
          this.setState({ efficiency: efficiency });
        }

        for (let field in beerProfile[0]) {
          if (nextProps.form.recipe.values[field] !== beerProfile[0][field]) {
            change(field, beerProfile[0][field]);
          }
        }
      }
    },
    300,
    { trailing: true },
  );

  updateWaterInStore = (totalWater, kettleWater) => {
    const { change } = this.props;
    change("water_amount", totalWater.toFixed(2));
    change("kettle_water", kettleWater.toFixed(2));
  };

  calculateWaterAmount = (nextProps) => {
    let allStagesWeight = 0;
    let mashStagesWeight = {};
    if (
      nextProps.form.recipe &&
      nextProps.form.recipe.values &&
      Array.isArray(nextProps.form.recipe.values.mashing)
    ) {
      nextProps.form.recipe.values.mashing.forEach((stage, index) => {
        let mashStageWeight = 0;
        if (stage && Array.isArray(stage.ingredient_additions)) {
          stage.ingredient_additions.forEach((ingredient) => {
            if (
              ingredient &&
              ingredient.fermentable_type == FERMENTABLE_TYPE.GRAIN
            ) {
              allStagesWeight += +ingredient.amount;
              mashStageWeight += +ingredient.amount;
            }
          });
        }
        mashStagesWeight[index] = mashStageWeight;
      });

      if (
        this.props.form.recipe &&
        this.props.form.recipe.values &&
        this.props.form.recipe.values.mashing !==
          nextProps.form.recipe.values.mashing
      ) {
        const newPreMixWater = calculateTotalMashInWater(allStagesWeight);
        const totalWater = calculateTotalWater(allStagesWeight);

        this.setState({
          preMixWater: newPreMixWater,
        });
        if (!isNaN(totalWater)) {
          this.updateWaterInStore(
            totalWater,
            calculateKettleWater(newPreMixWater, totalWater),
          );
          for (let stage in mashStagesWeight) {
            let newMashInWater = (mashStagesWeight[stage] / 1600).toFixed(2);
            if (
              nextProps.form.recipe.values.mashing[stage].mash_in_water !==
              newMashInWater
            ) {
              this.props.change(
                `mashing[${stage}].mash_in_water`,
                newMashInWater,
              );
            }
          }
        }
      }
    }
  };

  render() {
    const { change, isSaving, handleSubmit, valid, error } = this.props;
    const validationEnabled = false;
    const { batchSize, preMixWater, efficiency } = this.state;
    const totalWater = this.props.form.recipe.values
      ? this.props.form.recipe.values.water_amount
      : 0;
    const kettleWater = this.props.form.recipe.values
      ? this.props.form.recipe.values.kettle_water
      : 0;
    const totalFermentationSteps = countFermentationSteps(
      this.props.form.recipe,
    );
    const totalMashStages = countMashStages(this.props.form.recipe);
    if (this.state.netError) {
      return (
        <h2 className="error-msg">
          An error occurred while retrieving the recipe.
        </h2>
      );
    }

    if (this.state.isLoading) {
      return (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <Spinner className={"dark-spinner"} />
          <div>Loading recipe...</div>
        </div>
      );
    }

    return (
      <Form horizontal onSubmit={handleSubmit}>
        <div className="minibrew-container">
          <div className="recipe-header-row">
            <h2 className="title">Recipe details {this.state.weigth}</h2>
            <div className="recipe-top-actions hidden-xs">
              <ButtonWithLoad
                bsStyle="primary"
                bsSize="large"
                className="submit-btn"
                type="submit"
                isLoading={isSaving}
              >
                SAVE
              </ButtonWithLoad>
            </div>
          </div>
          <div className="share-recipe-col">
            <label className="share-recipe-label">
              <Field
                name="shared"
                type="checkbox"
                component="input"
                className="share-recipe-checkbox"
              />
              <span>Share recipe?</span>
            </label>
          </div>
          <p className="instruction">
            Currently the MiniBrew supports brewing on the basis of a 5.5 L
            target batch size. The amount of water required for the recipe is
            calculated based on the fermentables included in the mash, we use a
            1L/1.6kg ratio for in-mashing (though the water calculations account
            for more absorption). The MiniBrew can only handle grain milled to{" "}
            <b>
              Dry Basis Coarse Grind (
              <a href="https://byo.com/article/understanding-malt-spec-sheets-advanced-brewing/">
                DBCG
              </a>
              ) standards (0.7-1.0mm gap on a two roll mill)
            </b>
            . Please take these limitations and typical water absorption rates
            into consideration when calculating grain requirements for achieving
            a particular gravity. The serving temperature for your beer can be
            set at any value between 5ºC and 20ºC (out to one decimal place).
          </p>
        </div>
        <div className="minibrew-container" style={{ marginTop: 0 }}>
          <h2 style={{ marginTop: 0 }}>Notes</h2>
          <div className="row">
            <Field
              name="private_note"
              component={CustomField}
              componentClass="textarea"
              label="Private note"
              smSize={12}
              style={{ minHeight: 150 }}
            />
            <Field
              name="public_note"
              component={CustomField}
              componentClass="textarea"
              label="Public note"
              smSize={12}
              containerClassName="public-note-field"
            />
          </div>
        </div>
        <BrewWater
          batchSize={batchSize}
          totalWater={totalWater}
          preMixWater={preMixWater}
          kettleWater={kettleWater}
        />
        <div className="minibrew-container">
          <h2
            style={{
              fontWeight: "normal",
              fontFamily: "Gotham-Bold",
              marginTop: "3px",
            }}
          >
            Specifications
          </h2>
          <div className="row">
            <div className="col-md-6 col-sm-12">
              <p style={{ fontWeight: "normal", fontFamily: "Gotham-Book" }}>
                Target temperatures and carbonation level for serving and
                fermentation pitching.
              </p>
            </div>
            <div className="col-md-6 col-sm-12">
              <Field
                name="serving_temperature"
                component={CustomField}
                normalize={normalizeServingTemp}
                type="number"
                label="Serving temp. (ºC)"
                smSize={12}
                mdSize={12}
                step="0.01"
              />
              <Field
                name="chilling_temperature"
                component={CustomField}
                normalize={normalizeChillTemp}
                validate={chillTemp}
                type="number"
                label="Chill / pitch temp. (ºC)"
                smSize={12}
                mdSize={12}
                step="0.1"
              />
              <Field
                name="carbonation"
                component={CustomField}
                type="number"
                label="Carbonation (g/L CO2)"
                placeholder="e.g. 5.00"
                smSize={12}
                mdSize={12}
                step="0.01"
              />
              <div
                style={{
                  paddingLeft: "15px",
                  marginTop: "-10px",
                  marginBottom: "15px",
                }}
              >
                <a
                  href="#convert-co2"
                  className="co2-converter-link"
                  onClick={(e) => {
                    e.preventDefault();
                    this.setState({ showCo2ConverterModal: true });
                  }}
                  style={{
                    fontSize: "12px",
                    color: "#337ab7",
                    cursor: "pointer",
                    textDecoration: "underline",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <span
                    className="ion-calculator"
                    style={{ fontSize: "14px" }}
                  />
                  <span>Convert from vol. CO2</span>
                </a>
              </div>
            </div>
          </div>
        </div>
        <FieldArray
          name="mashing"
          component={Mashing}
          change={change}
          validationEnabled={validationEnabled}
          efficiency={efficiency}
          disableAddStages={totalMashStages >= MAX_MASH_STAGES}
        />
        <FieldArray
          name="boiling"
          user={this.props.userInfo.user}
          component={Boiling}
          change={change}
          validationEnabled={validationEnabled}
        />
        <FieldArray
          name="fermenting"
          component={Fermentation}
          change={change}
          validationEnabled={validationEnabled}
          disableAddSteps={totalFermentationSteps >= MAX_FERMENTING_STEPS}
        />
        {error && (
          <div style={{ margin: "20px 15px" }}>
            {error.map((err, index) => (
              <Alert bsStyle="danger" key={index}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "flex-row",
                    alignItems: "center",
                  }}
                >
                  <i
                    className="ion-android-alert"
                    style={{ fontSize: "24px", marginRight: "15px" }}
                  />{" "}
                  <div style={{ fontFamily: "Gotham-Book" }}>{err.message}</div>
                </div>
              </Alert>
            ))}
          </div>
        )}
        <div className="row">
          <div className="col-md-12">
            <div style={{ margin: "0px 20px" }}>
              {!valid && (
                <Error
                  error={
                    "Your recipe is currently not brewable, check the errors"
                  }
                />
              )}
            </div>
          </div>
        </div>
        <div
          className="button-row"
          style={{ margin: "20px 15px 80px", float: "right" }}
        >
          <ButtonWithLoad
            bsStyle="primary"
            bsSize="large"
            className="submit-btn"
            type="submit"
            isLoading={isSaving}
          >
            SAVE
          </ButtonWithLoad>
        </div>
        <Co2ConverterModal
          show={this.state.showCo2ConverterModal}
          onHide={() => this.setState({ showCo2ConverterModal: false })}
          onApply={this.handleApplyCo2Conversion}
          initialValue={
            this.props.form &&
            this.props.form.recipe &&
            this.props.form.recipe.values &&
            this.props.form.recipe.values.carbonation
          }
        />
      </Form>
    );
  }
}

const mapStateToProps = ({ form, router, userInfo }) => {
  return { form, routing: router, userInfo };
};

const mapDispatchToProps = (dispatch) => ({
  touch: (fields) => dispatch(touch("recipe", fields)),
});

Recipe = connect(mapStateToProps, mapDispatchToProps)(Recipe);

export default Recipe = reduxForm({
  form: "recipe",
  initialValues,
})(Recipe);
